import { Badge, Box, BoxProps, Button, ButtonProps, useDisclosure } from '@chakra-ui/react'

import React, { HTMLAttributeAnchorTarget, useContext } from 'react'
import { useApp } from "../hooks/useApp"
import { Add, Contract, Logout, News, Offer, Refresh, Settings, User } from '../icons'
import { IAvailableApps } from '../utils/availableApps'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { redirectToLogout } from '../hooks/useAccount'
import { insuranceParamsContext } from '../layouts/Wizard'
import { useOldAppPath } from '../hooks/useOldAppPath'
import { useGetGetAppInfoUnreadMessages } from 'BrokerTsLib/src/hooks/useAppInfoMessage'
import { baseUrl } from '../hooks'

interface MenuGroupProps {
  children: React.ReactNode
}

interface MenuItemProps extends ButtonProps {
  children: React.ReactNode
  href?: string
  to?: string
  variant?: string
  colorScheme?: string
  target?: HTMLAttributeAnchorTarget
}

interface SubMenuProps {
  button: React.ReactElement
  subMenu: React.ReactNode
  trigger?: 'mouse' | 'click'
}

export const MenuGroup = ({ children }: MenuGroupProps) => {
  return (
    <Box
      as="ul"
      sx={{
        display: 'grid',
        gridAutoFlow: 'column',
        gap: {
          xl: 1,
        },
        width: '100%',
        listStyle: 'none',
      }}
    >
      {children}
    </Box>
  )
}

export const MenuItem = ({ children, to, variant, colorScheme, as, sx, ...rest }: MenuItemProps) => {
  if (rest.href?.startsWith('/'))
    rest.href = baseUrl + rest.href

  return (
    <Button
      as={as ?? (!!rest.href ? 'a' : Link)}
      variant={variant || 'ghost'}
      colorScheme={colorScheme || 'primary'}
      to={to ?? ''}
      sx={{ gap: 1, position: 'relative', justifyContent: 'start', textAlign: 'start', ...sx }}
      {...rest}
    >
      {children}
    </Button>
  )
}

export const SubMenu = ({ children, ...rest }: BoxProps) => {
  return (
    <Box
      as="ul"
      sx={{
        p: 2,
        width: 'max-content',
        position: 'absolute',
        top: '100%',
        left: 0,
        bg: 'white',
        borderRadius: 'md',
        boxShadow: 'md',
        listStyle: 'none',
        zIndex: 'subMenu',
      }}
      {...rest}
    >
      {children}
    </Box>
  )
}

export const MenuItemWithSubMenu = ({ button, subMenu, trigger = 'mouse' }: SubMenuProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  let props: object = {}
  if (trigger == 'mouse')
    props = { ...props, onMouseEnter: onOpen, onMouseLeave: onClose }

  if (trigger == 'click')
    props = { ...props, onClick: () => isOpen ? onClose() : onOpen() }

  return (
    <Box
      as='ul'
      sx={{ gap: 1, position: 'relative', justifyContent: 'start', textAlign: 'start', listStyle: 'none', }}
      {...props}
    >
      {button && button}

      {subMenu && isOpen && subMenu}
    </Box>
  )
}

const NewInsuranceButtonCarkulka = ({ app }: { app: IAvailableApps['appId'] }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  let closeTimeout: NodeJS.Timeout

  const { setTriggerNewInsurance } = useContext(insuranceParamsContext)

  return (
    <Box
      sx={{ position: 'relative' }}
      onMouseEnter={() => {
        clearTimeout(closeTimeout)
        onOpen()
      }}
      onMouseLeave={() => {
        closeTimeout = setTimeout(() => onClose(), 300)
      }}
    >
      <MenuItem variant="solid" colorScheme="primary" to={`/${app}/wizard/1`} onClick={() => setTriggerNewInsurance(true)}>
        <Add />
        Nové pojištění
      </MenuItem>

      {isOpen && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            pt: 2,
            w: 'max-content',
          }}
        >
          <MenuItem
            variant="outline"
            colorScheme="gray"
            to={`/${app}/extra/wizard/1`}
            onClick={() => setTriggerNewInsurance(true)}
            sx={{ boxShadow: 'md' }}
          >
            <Add color="secondary.500" />
            Nové pojištění doplňků
          </MenuItem>
        </Box>
      )}
    </Box>
  )
}

const NewInsuranceButton = ({ app }: { app: IAvailableApps['appId'] }) => {
  const { setTriggerNewInsurance } = useContext(insuranceParamsContext)

  if (app === 'carkulka') {
    return <NewInsuranceButtonCarkulka app={app} />
  }

  return (
    <MenuItem variant="solid" colorScheme="primary" to={`/${app}/wizard/1`} onClick={() => setTriggerNewInsurance(true)}>
      <Add />
      Nové pojištění
    </MenuItem>
  )
}

export const Menu = () => {
  const { app, product } = useApp()
  const menuApp = app === 'carkulka-extra' ? 'carkulka' : app!

  const { user } = useAuth() ?? {}
  const carkulkaUrl = process.env.REACT_APP_CARKULKA_URL ?? ''
  const oldAppLocation = useOldAppPath()

  const { data: messages } = useGetGetAppInfoUnreadMessages(product!);

  const newsCount = messages?.data ?? 0

  return (
    <Box
      sx={{
        display: { base: 'none', xl: 'grid' },
        width: '100%',
        gridAutoFlow: 'column',
        justifyContent: 'space-between',
        justifyItems: 'space-between',
      }}
      className="site-menu-wrapper"
    >
      <MenuGroup>
        <li>
          <NewInsuranceButton app={menuApp} />
        </li>
        <li>
          <MenuItem to={`/${menuApp}/concepts`}>
            <Offer color="secondary.500" />
            Nabídky
          </MenuItem>
        </li>
        <li>
          <MenuItem to={`/${menuApp}/contracts`}>
            <Contract color="secondary.500" />
            Smlouvy
          </MenuItem>
        </li>

        {(user?.settingsMenu && user.settingsMenu.length > 0 || oldAppLocation) &&
          <li>
            <MenuItemWithSubMenu
              button={
                <MenuItem>
                  <Settings color="secondary.500" />
                  Nastavení
                </MenuItem>
              }
              subMenu={
                <SubMenu>
                  {user && user.settingsMenu!.map(item => {
                    return (
                      <li key={item.url}>
                        <MenuItem href={item.url}>{item.name}</MenuItem>
                      </li>
                    )
                  })}
                  {oldAppLocation &&
                    <li>
                      <MenuItem variant='outline' colorScheme='gray' href={carkulkaUrl + '/' + oldAppLocation}>
                        <Refresh color="secondary.500" />
                        Přepnout na předchozí verzi aplikace
                      </MenuItem>
                    </li>}
                </SubMenu>
              }
            />
          </li>
        }
      </MenuGroup>

      <MenuGroup>
        {/* <li>
          <MenuItem to="/faq">
            <Help color="secondary.500" />
            Nápověda
          </MenuItem>
        </li> */}
        <li>
          <MenuItem to={`/${menuApp}/news`}>
            <News color="secondary.500" />
            Aktuality
            <Badge>{newsCount}</Badge>
          </MenuItem>
        </li>
        <li>
          <MenuItem {...(!!user?.vehicleRights
            ? {
              to: `/profile`
            }
            : {
              as: "div",
              isDisabled: true
            })
          }
          >
            <User color="secondary.500" />
            {user?.userFullName}
          </MenuItem>
        </li>
        <li>
          <MenuItem as={'button'} onClick={() => redirectToLogout()}>
            <Logout color="secondary.500" />
          </MenuItem>
        </li>
      </MenuGroup>
    </Box>
  )
}
